export const MCP_SUGGESTION_MARKER_PREFIX = '__mcp_suggestion__:';
export const MCP_COLLECT_MARKER_PREFIX = '__mcp_collect__:';

const SUGGESTION_MARKER_RE = /__mcp_suggestion__:([^\s\n]+)/;
const COLLECT_MARKER_RE = /__mcp_collect__:(\{[^\n]*\})/;

export function buildSuggestionMarker(key: string): string {
  return `${MCP_SUGGESTION_MARKER_PREFIX}${key.trim()}`;
}

export function buildCollectMarker(values: Record<string, string>): string {
  return `${MCP_COLLECT_MARKER_PREFIX}${JSON.stringify(values)}`;
}

/** Visible user text + hidden markers for server-side injection. */
export function buildSuggestionUserMessage(
  visibleText: string,
  suggestionKey: string,
  collectValues?: Record<string, string> | null
): string {
  const parts: string[] = [];
  const visible = visibleText.trim();
  if (visible) parts.push(visible);
  parts.push(buildSuggestionMarker(suggestionKey));
  if (collectValues && Object.keys(collectValues).length > 0) {
    parts.push(buildCollectMarker(collectValues));
  }
  return parts.join('\n');
}

export function parseSuggestionMarker(
  content: string | null | undefined
): string | null {
  if (!content) return null;
  const match = content.match(SUGGESTION_MARKER_RE);
  const key = match?.[1]?.trim();
  return key || null;
}

export function parseCollectMarker(
  content: string | null | undefined
): Record<string, string> | null {
  if (!content) return null;
  const match = content.match(COLLECT_MARKER_RE);
  const raw = match?.[1]?.trim();
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(
      parsed as Record<string, unknown>
    )) {
      if (typeof value === 'string' && value.trim()) {
        out[key] = value.trim();
      }
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

export type ChatSuggestionRequest = {
  key: string;
  values?: Record<string, string>;
};

/**
 * Markers only carry the suggestion inside the panel. On the way out they are
 * stripped from the user text and sent as the typed `suggestion` field.
 */
export function extractSuggestionRequest(content: string): {
  content: string;
  suggestion: ChatSuggestionRequest | null;
} {
  const key = parseSuggestionMarker(content);
  if (!key) return { content, suggestion: null };
  const values = parseCollectMarker(content);
  return {
    // A suggestion sent without visible text keeps its raw form.
    content: displaySuggestionUserMessage(content) || content,
    suggestion: values ? { key, values } : { key }
  };
}

/** Strip hidden suggestion/collect markers (including truncated thread titles). */
const COLLECT_MARKER_DISPLAY_RE = /__mcp_collect__:[\s\S]*/g;

export function displaySuggestionUserMessage(
  content: string | null | undefined
): string {
  if (!content) return '';
  return content
    .replace(SUGGESTION_MARKER_RE, '')
    .replace(COLLECT_MARKER_RE, '')
    .replace(COLLECT_MARKER_DISPLAY_RE, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}
